import type { IMatchDetails, IPlayersTeamSquad, ITeamsSquad, ISubsitutes} from "./types/matchDetails.d";
import { Images } from "../../../../../../utils/imageConstants";
import { formatPlayerRoleLabel, getPlayerImageUrl } from "../../../../../../helper/helper";
import { useOpenPlayerProfile } from "./useOpenPlayerProfile";

interface PlayersProps {
  matchSummary: IMatchDetails | undefined;
  matchTab:string;
  teamOnePlayers: IPlayersTeamSquad | undefined;
  teamTwoPlayers: IPlayersTeamSquad | undefined;
  matchStatus?: string;
}

const MatchesPlayers = ({ matchSummary, teamOnePlayers, teamTwoPlayers, matchStatus }: PlayersProps) => {
  const isUpcoming = matchStatus === "UPCOMING" || matchStatus === "SCHEDULED";
  const openPlayerProfile = useOpenPlayerProfile();

  const getPlayerRole = (playerRole: string | null | undefined): string => {
    if (!playerRole) return "Player";
    const roleMap: { [key: string]: string } = {
      "BAT": "Batter",
      "BOWL": "Bowler",
      "WK": "Wicketkeeper",
      "AR": "All-Rounder",
      "ALL": "All-Rounder",
    };
    
    // Check if role contains common patterns
    const roleUpper = playerRole.toUpperCase();
    if (roleUpper.includes("BATTER") || roleUpper.includes("BAT")) {
      if (roleUpper.includes("TOP") || roleUpper.includes("OPEN")) return "Top Order Batter";
      if (roleUpper.includes("MIDDLE")) return "Middle Order Batter";
      if (roleUpper.includes("WK") || roleUpper.includes("WICKET")) return "Wicketkeeper Batter";
      return "Batter";
    }
    if (roleUpper.includes("BOWL") || roleUpper.includes("BOWLER")) return "Bowler";
    if (roleUpper.includes("WK") || roleUpper.includes("WICKET")) return "Wicketkeeper";
    if (roleUpper.includes("ALL") || roleUpper.includes("ROUNDER")) return "All-Rounder";
    
    return roleMap[roleUpper] || formatPlayerRoleLabel(playerRole);
  };

  const getPlayersToDisplay = (players: IPlayersTeamSquad | undefined): (ITeamsSquad | ISubsitutes)[] => {
    if (!players) return [];
    if (players.playingSquad && players.playingSquad.length > 0) {
      return players.playingSquad;
    }
    return players.bench || [];
  };

  const renderPlayerListItem = (player: ITeamsSquad | ISubsitutes, idx: number, keyPrefix: string) => {
    const squadPlayer = player as Partial<ITeamsSquad>;
    const roleKey = (player?.playerRole ?? "").trim().toUpperCase();
    const isCaptain = roleKey === "CAPTAIN" || roleKey === "TEAM_CAPTAIN";
    const isViceCaptain = roleKey === "VICE_CAPTAIN" || roleKey === "VICECAPTAIN" || roleKey.includes("VICE");
    const isWk = roleKey === "WICKET_KEEPER" || roleKey === "WICKETKEEPER" || (roleKey.includes("WICKET") && roleKey.includes("KEEP"));

    const battingStyle = (squadPlayer.battingStyle ?? "").trim().toUpperCase();
    const bowlingStyle = (squadPlayer.bowlingStyle ?? "").trim().toUpperCase();
    const hasBatSkill = Boolean(battingStyle && battingStyle !== "NULL");
    const hasBowlSkill = Boolean(bowlingStyle && bowlingStyle !== "NULL");
    const showBatIcon = hasBatSkill || roleKey.includes("BAT") || roleKey.includes("ALL_ROUNDER");
    const showBallIcon = hasBowlSkill || roleKey.includes("BOWL") || roleKey.includes("ALL_ROUNDER");

    const playerName = `${player?.playerFirstName ?? ""} ${player?.playerLastName ?? ""}`.trim() || "Player";

    return (
      <div
        key={`${keyPrefix}-${player?.playerId || idx}`}
        className="player-list-item player-list-item-clickable"
        role="button"
        tabIndex={0}
        onClick={() => openPlayerProfile(player?.playerId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPlayerProfile(player?.playerId);
          }
        }}
      >
        <div className="player-list-avatar">
          <img
            src={getPlayerImageUrl(player?.imageUri) || Images.player_image}
            alt={playerName}
          />
          {isCaptain && (
            <span className="player-list-badge captain" title="Captain">C</span>
          )}
          {isViceCaptain && (
            <span className="player-list-badge vice" title="Vice-captain">VC</span>
          )}
        </div>
        <div className="player-list-info">
          <div className="player-list-name-row">
            <span className="player-list-name">{playerName}</span>
            {isWk && (
              <img className="player-list-wk-icon" src={Images.hands_icon} alt="" title="Wicketkeeper" />
            )}
          </div>
          <span className="player-list-role">{getPlayerRole(player?.playerRole)}</span>
        </div>
        <div className="player-list-skills" aria-hidden="true">
          {showBallIcon && <img src={Images.ball_icon} alt="" />}
          {showBatIcon && <img src={Images.bat_icon} alt="" />}
        </div>
      </div>
    );
  };

  const renderPlayersList = (players: (ITeamsSquad | ISubsitutes)[], keyPrefix: string, emptyLabel: string) => (
    <div className="players-list" role="list" aria-label="Players list">
      {players.length
        ? players.map((player, idx) => renderPlayerListItem(player, idx, keyPrefix))
        : <div className="players-list-empty">{emptyLabel}</div>}
    </div>
  );


  if (isUpcoming) {
    const teamOnePlayersList = getPlayersToDisplay(teamOnePlayers);
    const teamTwoPlayersList = getPlayersToDisplay(teamTwoPlayers);

    return (
      <section aria-label="Match squads">
        <div className="upcoming-squads-wrapper">
          {/* Team 1 Column */}
          <div className="upcoming-squad-column">
            <div className="upcoming-squad-header">
              <p>{matchSummary?.teamOne?.teamName?.toUpperCase() ?? "TEAM 1"}</p>
            </div>
            <div className="upcoming-squad-list">
              {teamOnePlayersList.map((player, idx) => (
                <div
                  key={player?.playerId || idx}
                  className="upcoming-squad-player-item upcoming-squad-player-item-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => openPlayerProfile(player?.playerId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPlayerProfile(player?.playerId);
                    }
                  }}
                >
                  <div className="upcoming-squad-player-info">
                    <div className="upcoming-squad-player-image-wrapper">
                      <img 
                        src={getPlayerImageUrl(player?.imageUri) || Images.player_image} 
                        alt={player?.playerFirstName || "Player"} 
                      />
                    </div>
                    <div className="upcoming-squad-player-details">
                      <p className="upcoming-squad-player-name">
                        {player?.playerFirstName} {player?.playerLastName}
                      </p>
                      <p className="upcoming-squad-player-role">
                        {getPlayerRole(player?.playerRole)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team 2 Column */}
          <div className="upcoming-squad-column">
            <div className="upcoming-squad-header">
              <p>{matchSummary?.teamTwo?.teamName?.toUpperCase() ?? "TEAM 2"}</p>
            </div>
            <div className="upcoming-squad-list">
              {teamTwoPlayersList.map((player, idx) => (
                <div
                  key={player?.playerId || idx}
                  className="upcoming-squad-player-item upcoming-squad-player-item-clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => openPlayerProfile(player?.playerId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPlayerProfile(player?.playerId);
                    }
                  }}
                >
                  <div className="upcoming-squad-player-info">
                    <div className="upcoming-squad-player-image-wrapper no-border">
                      <img 
                        src={getPlayerImageUrl(player?.imageUri) || Images.player_image} 
                        alt={player?.playerFirstName || "Player"} 
                      />
                    </div>
                    <div className="upcoming-squad-player-details">
                      <p className="upcoming-squad-player-name">
                        {player?.playerFirstName} {player?.playerLastName}
                      </p>
                      <p className="upcoming-squad-player-role">
                        {getPlayerRole(player?.playerRole)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Match players">
      <div className="players-wrapper">
        {/* Team 1 */}
        <section
          className="team-one"
          aria-labelledby="team-one-name"
          aria-describedby="team-one-description"
        >
          <h2 id="team-one-name" className="team-name">
            {matchSummary?.teamOne?.teamName ?? "-"}
          </h2>

          <section className="playing-teams" aria-label="Playing XI">
            {/* keep <p> for styling, but give it heading semantics */}
            <p role="heading" aria-level={3}>Playing Xl</p>
            {renderPlayersList(getPlayersToDisplay(teamOnePlayers), "t1-playing", "No players listed")}
          </section>

          <section className="playing-teams" aria-label="Substitutes">
            <p role="heading" aria-level={3}>Substitutes</p>
            {renderPlayersList(teamOnePlayers?.bench ?? [], "t1-sub", "No substitutes listed")}
          </section>

          {/* hidden description hook if needed by SRs */}
          <span id="team-one-description" hidden>
            Playing XI and substitutes for Sunrises Hyderabad
          </span>
        </section>

        {/* Team 2 */}
        <section
          className="team-one"
          aria-labelledby="team-two-name"
          aria-describedby="team-two-description"
        >
          <h2 id="team-two-name" className="team-name">
             {matchSummary?.teamTwo?.teamName ?? "-"}
          </h2>

          <section className="playing-teams" aria-label="Playing XI">
            <p role="heading" aria-level={3}>Playing Xl</p>
            {renderPlayersList(getPlayersToDisplay(teamTwoPlayers), "t2-playing", "No players listed")}
          </section>

          <section className="playing-teams" aria-label="Substitutes">
            <p role="heading" aria-level={3}>Substitutes</p>
            {renderPlayersList(teamTwoPlayers?.bench ?? [], "t2-sub", "No substitutes listed")}
          </section>

          <span id="team-two-description" hidden>
            Playing XI and substitutes for Royal Challengers Bangalore
          </span>
        </section>
      </div>
    </section>
  );
};

export default MatchesPlayers;
